import { useState } from "react";
import axios from "axios";
import { Star, CheckCircle2, Loader2, MessageSquarePlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABELS = ["", "Muy poco útil", "Poco útil", "Útil", "Muy útil", "¡Excelente!"];

export default function ReportFeedback({ analysisId }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!rating) {
      setError("Selecciona una puntuación con las estrellas.");
      return;
    }
    setSending(true);
    setError(null);
    try {
      await axios.post(`${API}/report-feedback`, { analysis_id: analysisId, rating, comment: comment.trim() });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo enviar. Intenta de nuevo.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid="report-feedback-done" className="bg-white rounded-2xl shadow-lg border border-purple-100 p-6 text-center">
        <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
        <p className="font-semibold text-gray-700">¡Gracias por tu opinión! Nos ayuda a mejorar cada informe.</p>
      </div>
    );
  }

  return (
    <div data-testid="report-feedback" className="bg-white rounded-2xl shadow-lg border border-purple-100 p-6">
      <h3 className="font-bold text-gray-800 mb-1 flex items-center gap-2">
        <MessageSquarePlus className="w-5 h-5 text-purple-600" /> ¿Qué tan útil fue este informe?
      </h3>
      <div className="flex items-center gap-1 my-3">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            data-testid={`report-star-${n}`}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => setRating(n)}
            aria-label={`${n} estrellas`}
            className="p-1 transition-transform hover:scale-125"
          >
            <Star className={`w-7 h-7 ${(hover || rating) >= n ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`} />
          </button>
        ))}
        {(hover || rating) > 0 && (
          <span className="ml-2 text-sm font-semibold text-gray-600">{LABELS[hover || rating]}</span>
        )}
      </div>
      <label className="block text-sm">
        <span className="block text-xs font-bold text-gray-500 uppercase mb-1">¿Qué mejorarías?</span>
        <textarea
          data-testid="report-feedback-comment"
          rows={2}
          maxLength={1000}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Cuéntanos qué te gustaría ver en el informe..."
          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
        />
      </label>
      {error && <p data-testid="report-feedback-error" className="text-xs text-red-600 font-semibold mt-2">{error}</p>}
      <button
        data-testid="report-feedback-submit"
        onClick={submit}
        disabled={sending}
        className="mt-3 flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-60 transition-all"
      >
        {sending && <Loader2 className="w-4 h-4 animate-spin" />} Enviar opinión
      </button>
    </div>
  );
}
